import React, { Suspense, useEffect } from "react";
import { Toaster } from "@/shared/ui/toaster";
import { Toaster as Sonner } from "@/shared/ui/sonner";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { queryClient } from "@/lib/react-query";
import { indexedDBPersister, dehydrateOptions } from "@/lib/queryPersister";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/core/auth/useAuth";
import { isSmartphone } from "@/shared/utils/deviceDetection";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ImpersonationProvider } from "@/core/auth/useImpersonation";
import { MutationQueueProvider } from "@/contexts/MutationQueueContext";
import ImpersonationBanner from "@/shared/components/ImpersonationBanner";
import ProtectedRoute from "@/shared/components/ProtectedRoute";
import KioskRoute from "@/shared/components/KioskRoute";
import { ScrollToTop } from "@/shared/components/ScrollToTop";
import { MainLayout } from "@/shared/layouts/MainLayout";
import { StandaloneLayout } from "@/shared/layouts/StandaloneLayout";
import ServiceWorkerUpdateNotification from "@/shared/components/ServiceWorkerUpdateNotification";
import ErrorBoundary from "@/shared/components/ErrorBoundary";
import { LazyOfflineBoundary } from "@/shared/components/LazyOfflineFallback";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";



// GUARDIAN Performance Monitoring System
import '@/utils/guardianInit';
// Operation Timing Batcher — auto-starts flush every 30s
import { startTimingBatcher } from '@/utils/operationTimer';
startTimingBatcher();
// Lazy load test component to avoid errors if file doesn't exist
const TestEncryption = React.lazy(() =>
  import("@/pages/TestEncryption")
);

// Lazy load TestCrypto component
const TestCrypto = React.lazy(() =>
  import("@/pages/TestCrypto")
);
// Lazy load TestCircularDeps component
const TestCircularDeps = React.lazy(() =>
  import("@/pages/TestCircularDeps")
);

// Lazy load MealStatisticsDashboard for admin
const MealStatisticsDashboard = React.lazy(() =>
  import("@/pages/admin/MealStatisticsDashboard")
);

// Lazy load CFA Management pages
const CFAManagementPage = React.lazy(() =>
  import("@/pages/admin/cfa/CFAManagementPage")
);
const CFADetailPage_ = React.lazy(() =>
  import("@/pages/admin/cfa/CFADetailPage")
);
// --- All page components lazy-loaded for code splitting ---
// Core pages
const Index = React.lazy(() => import("./pages/Index"));
const Auth = React.lazy(() => import("./pages/Auth"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const AuthTest = React.lazy(() => import("./pages/AuthTest"));
const MFASetup = React.lazy(() => import("./pages/auth/MFASetup"));
const MFAVerify = React.lazy(() => import("./pages/auth/MFAVerify"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const AdminUsers = React.lazy(() => import("./pages/AdminUsers"));
const RichiedentiAsilo = React.lazy(() => import("./pages/RichiedentiAsilo"));
const RADetailPage = React.lazy(() => import("./pages/RADetailPage"));
const RAEditPage = React.lazy(() => import("./pages/RAEditPage"));
const RACreatePage = React.lazy(() => import("./pages/RACreatePage"));

// Shop pages
const Shop = React.lazy(() => import('./pages/Shop'));
const ShopConsegnaProdotti = React.lazy(() => import('./pages/shop/ConsegnaProdotti'));
const ShopBigliettiTreno = React.lazy(() => import('./pages/shop/BigliettiTreno'));
const ShopLavanderia = React.lazy(() => import('./pages/shop/Lavanderia'));
const GestioneProdottiEdit = React.lazy(() => import('./pages/shop/GestioneProdottiEdit'));
const LaundryManagement = React.lazy(() => import('./pages/shop/LaundryManagement'));
const LaundryManagementV2 = React.lazy(() => import('./pages/shop/LaundryManagementV2'));
const LaundryNetHistory = React.lazy(() => import('./pages/shop/LaundryNetHistory'));
const LaundryForgottenTickets = React.lazy(() => import('./pages/shop/LaundryForgottenTickets'));

const TransferVerificationPage = React.lazy(() => import('./pages/shop/TransferVerificationPage'));
const DeviceLogin = React.lazy(() => import('./pages/device/DeviceLogin'));
const DevicePairing = React.lazy(() => import('./pages/device/DevicePairing'));
const DeviceManagement = React.lazy(() => import('./pages/admin/DeviceManagement'));
const TicketInventoryManagement = React.lazy(() =>
  import('./pages/shop/TicketInventoryManagement').then(m => ({ default: m.TicketInventoryManagement }))
);
const ShopRADetail = React.lazy(() => import('./pages/shop/ShopRADetail'));
const GestioneRestituzioni = React.lazy(() => import('./pages/shop/GestioneRestituzioni'));
const ShopStatistiche = React.lazy(() => import('./pages/shop/ShopStatistiche'));
const ShopGroupDelivery = React.lazy(() => import('./pages/shop/ShopGroupDelivery'));

// Mobile pages
const MobileBigliettiTreno = React.lazy(() => import('./pages/mobile/MobileBigliettiTreno'));
const MobileBigliettiTrenoGroup = React.lazy(() => import('./pages/mobile/MobileBigliettiTrenoGroup'));

// Super Admin pages
const SuperAdminLayout = React.lazy(() => import('./features/super-admin/components/SuperAdminLayout'));
const SuperAdminOverviewPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminOverviewPage'));
const SuperAdminUsersPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminUsersPage'));
const AccessiPage = React.lazy(() => import('./features/super-admin/pages/AccessiPage'));
const SuperAdminKioskPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminKioskPage'));
const SuperAdminCfaPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminCfaPage'));
const SuperAdminCfaDetailPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminCfaDetailPage'));
const SuperAdminAuditPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminAuditPage'));
const SuperAdminSessionsPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminSessionsPage'));
const SuperAdminTimeSavingsPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminTimeSavingsPage'));
const SuperAdminDataRetentionPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminDataRetentionPage'));
const SuperAdminInfoboardPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminInfoboardPage'));
const SuperAdminShopStatsPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminShopStatsPage'));
const SuperAdminSystemMapPage = React.lazy(() => import('./features/super-admin/pages/SuperAdminSystemMapPage'));

// Capogruppo — Sidebar layout with nested routes
const CapogruppoLayout = React.lazy(() => import('./features/capogruppo/components/CapogruppoLayout'));
const CapogruppoPanoramicaPage = React.lazy(() => import('./features/capogruppo/pages/CapogruppoPanoramicaPage'));
const CapogruppoTaskPage = React.lazy(() => import('./features/capogruppo/pages/CapogruppoTaskPage'));
const CapogruppoScabbiaPage = React.lazy(() => import('./features/capogruppo/pages/CapogruppoScabbiaPage'));
const CapogruppoStockPage = React.lazy(() => import('./features/capogruppo/pages/CapogruppoStockPage'));
const CapogruppoInfoboardPage = React.lazy(() => import('./features/capogruppo/pages/CapogruppoInfoboardPage'));

// Admin / Features pages — Amministrazione Layout + Pages
const AmministrazioneLayout = React.lazy(() => import('./features/amministrazione/components/AmministrazioneLayout'));
const AmministrazioneOverviewPage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneOverviewPage'));
const AmministrazioneRaPage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneRaPage'));
const AmministrazioneProdottiPage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneProdottiPage'));
const AmministrazioneAbbigliamentoPage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneAbbigliamentoPage'));
const AmministrazioneTransferPage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneTransferPage'));

const AmministrazioneElenchiPage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneElenchiPage'));
const AmministrazioneReportPage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneReportPage'));
const AmministrazioneMensaPage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneMensaPage'));
const AmministrazioneRamadanPage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneRamadanPage'));
const EmailProceduresPage = React.lazy(() => import('./features/email-procedures/pages/EmailProceduresPage'));
const SuperAdminDashboard = React.lazy(() => import('./pages/SuperAdminDashboard'));
const AdminAnomalyPanel = React.lazy(() => import('./features/amministrazione/components/anomalies/AdminAnomalyPanel'));
const AmministrazioneTaskOperativiPage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneTaskOperativiPage'));
const AmministrazioneTempCardsPage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneTempCardsPage'));
const AmministrazioneVenditePage = React.lazy(() => import('./features/amministrazione/pages/AmministrazioneVenditePage'));
const CapogruppoTaskOperativiPage = React.lazy(() => import('./features/capogruppo/pages/CapogruppoTaskOperativiPage'));

// Educatori Layout + Pages
const EducatoriLayout = React.lazy(() => import('./features/educatori/components/EducatoriLayout'));
const EducatoriOverviewPage = React.lazy(() => import('./features/educatori/pages/EducatoriOverviewPage'));
const EducatoriRaPage = React.lazy(() => import('./features/educatori/pages/EducatoriRaPage'));
const EducatoriRaDetailPage = React.lazy(() => import('./features/educatori/pages/EducatoriRaDetailPage'));
const EducatoriHygienePage = React.lazy(() => import('./features/educatori/pages/EducatoriHygienePage'));
const EducatoriWatchlistDetailPage = React.lazy(() => import('./features/educatori/pages/EducatoriWatchlistDetailPage'));

// PWA pages
const MobileAppRequired = React.lazy(() => import('@/pages/MobileAppRequired'));
const PwaDemoPage = React.lazy(() => import('@/pages/pwa/PwaDemoPage'));
const PwaTypographyShowcase = React.lazy(() => import('@/pages/pwa/PwaTypographyShowcase'));

// Clothing pages
const ClothingStockPage = React.lazy(() => import('./pages/clothing/ClothingStockPage'));
const ClothingRequestsPage = React.lazy(() => import('./pages/clothing/ClothingRequestsPage'));
const ClothingRequestCreatePage = React.lazy(() => import('./pages/clothing/ClothingRequestCreatePage'));
const ClothingArrivalRegistrationPage = React.lazy(() =>
  import('./pages/clothing/ClothingArrivalRegistrationPage').then(m => ({ default: m.ClothingArrivalRegistrationPage }))
);

// Scanner / Test
const ScannerTestPage = React.lazy(() => import('./pages/ScannerTest'));

// Appointment pages
const AppointmentsManagement = React.lazy(() => import('./pages/appointments/AppointmentsManagement'));
const AppointmentCategoriesAdmin = React.lazy(() => import('./pages/appointments/AppointmentCategoriesAdmin'));
const AppointmentCreatePage = React.lazy(() => import('./pages/appointments/AppointmentCreatePage'));
const AppointmentImportRLPage = React.lazy(() => import('./pages/appointments/AppointmentImportRLPage'));
const AppointmentImportSEMPage = React.lazy(() => import('./pages/appointments/AppointmentImportSEMPage'));

// GEP Pages
const GepDashboard = React.lazy(() => import('./pages/gep/GepDashboard'));
const GepEligibleList = React.lazy(() => import('./pages/gep/GepEligibleList'));
const GepActivitiesConfig = React.lazy(() => import('./pages/gep/GepActivitiesConfig'));
const GepActivityTypesManagement = React.lazy(() => import('./pages/gep/GepActivityTypesManagement'));
const GepCollaboratorsManagement = React.lazy(() => import('./pages/gep/GepCollaboratorsManagement'));
const GepWorkDayRegistration = React.lazy(() => import('./pages/gep/GepWorkDayRegistration'));
const GepRAPointsList = React.lazy(() => import('./pages/gep/GepRAPointsList'));
const GepConfigAdmin = React.lazy(() => import('./pages/gep/GepConfigAdmin'));
const GepActivityRecordPage = React.lazy(() => import('./pages/gep/GepActivityRecordPage'));
const GepDailyActivityCategoriesManagement = React.lazy(() => import('./pages/gep/GepDailyActivityCategoriesManagement'));
const DistributionPage = React.lazy(() => import('./pages/distribution/DistributionPage'));

// Kiosk Pages (lazy loaded)
const KioskMealPage = React.lazy(() => import("@/pages/kiosk/KioskMealPage"));
const KioskDemoPage = React.lazy(() => import("@/pages/kiosk/KioskDemoPage"));

// Redirect /admin/cfa/:cfaId → /admin/super/cfa/:cfaId (preserves param)
const CfaDetailRedirect = () => {
  const { cfaId } = useParams<{ cfaId: string }>();
  return <Navigate to={`/admin/super/cfa/${cfaId}`} replace />;
};

// Redirect smartphone (non tablet) a /pwa — super-admin escluso
const SmartphoneRedirect = () => {
  const { user, isLoading, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && user && isSmartphone() &&
      !hasRole('super-admin') &&
      !location.pathname.startsWith('/mobile') &&
      !location.pathname.startsWith('/pwa') &&
      !location.pathname.startsWith('/auth') &&
      !location.pathname.startsWith('/shop') &&
      !location.pathname.startsWith('/admin')) {
      navigate('/mobile', { replace: true });
    }
  }, [isLoading, user, location.pathname, navigate, hasRole]);

  return null;
};

const App = () => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister: indexedDBPersister,
      maxAge: 24 * 60 * 60 * 1000, // 24 hours
      buster: 'v1', // Increment to invalidate cache on breaking changes
      dehydrateOptions,
    }}
  >
    <ThemeProvider defaultTheme="light">
      <AccessibilityProvider>
        <TooltipProvider>
          <BrowserRouter>
            <AuthProvider>
                <MutationQueueProvider>
                  <ImpersonationProvider>
                    <ImpersonationBanner />
                    <SmartphoneRedirect />
                    <ScrollToTop />
                    <Toaster />
                    <Sonner />
                    <ServiceWorkerUpdateNotification />
                    <LazyOfflineBoundary>
                    <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-4 border-dashed rounded-full animate-spin border-gray-400"></div></div>}>
                    <Routes>
                      {/* Standalone routes without header */}
                      <Route element={<StandaloneLayout />}>
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/mobile" element={<MobileAppRequired />} />
                        <Route path="/auth/reset-password" element={<ResetPassword />} />
                        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                        <Route path="/auth/mfa-setup" element={<MFASetup />} />
                        <Route path="/auth/mfa-verify" element={<MFAVerify />} />
                        <Route path="/auth-test" element={<AuthTest />} />
                        <Route path="/device" element={<DeviceLogin />} />
                        <Route path="/pair" element={<DevicePairing />} />
                        <Route
                          path="/admin/devices"
                          element={
                            <ProtectedRoute requiredRoles={["super-admin"]}>
                              <DeviceManagement />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/scanner-test" element={<ScannerTestPage />} />
                        {/* Kiosk routes - standalone, no header */}
                        <Route path="/kiosk" element={<Navigate to="/kiosk/meals" replace />} />
                        <Route
                          path="/kiosk/meals"
                          element={
                            <KioskRoute>
                              <Suspense fallback={<div className="flex items-center justify-center h-screen bg-slate-50"><div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-black"></div></div>}>
                                <KioskMealPage />
                              </Suspense>
                            </KioskRoute>
                          }
                        />
                        <Route
                          path="/kiosk/demo"
                          element={
                            <KioskRoute>
                              <Suspense fallback={<div className="flex items-center justify-center h-screen bg-slate-50"><div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-black"></div></div>}>
                                <KioskDemoPage />
                              </Suspense>
                            </KioskRoute>
                          }
                        />
                        <Route
                          path="/pwa/*"
                          element={
                            <ProtectedRoute>
                              <Suspense fallback={<div className="flex items-center justify-center h-screen bg-slate-50"><div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-black"></div></div>}>
                                <PwaDemoPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/pwa-typography"
                          element={
                            <ProtectedRoute>
                              <Suspense fallback={<div className="flex items-center justify-center h-screen bg-slate-50"><div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-black"></div></div>}>
                                <PwaTypographyShowcase />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                      </Route>

                      {/* Main routes with header */}
                      <Route element={<MainLayout />}>
                        <Route path="/" element={<Index />} />
                        <Route
                          path="/admin/users"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin"]}>
                              <AdminUsers />
                            </ProtectedRoute>
                          }
                        />
                        {/* Super Admin — Sidebar layout with nested routes */}
                        <Route
                          path="/admin/super"
                          element={
                            <ProtectedRoute requiredRoles={["super-admin"]}>
                              <SuperAdminLayout />
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<SuperAdminOverviewPage />} />
                          <Route path="users" element={<SuperAdminUsersPage />} />
                          {/* Accessi — gate ripetuto di proposito: la pagina governa i permessi
                              di tutti, quindi non deve dipendere solo dalla rotta padre. */}
                          <Route
                            path="accessi"
                            element={
                              <ProtectedRoute requiredRoles={["super-admin"]}>
                                <AccessiPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route path="kiosk" element={<SuperAdminKioskPage />} />
                          <Route path="cfa" element={<SuperAdminCfaPage />} />
                          <Route path="cfa/:cfaId" element={<SuperAdminCfaDetailPage />} />
                          <Route path="audit" element={<SuperAdminAuditPage />} />
                          <Route path="sessions" element={<SuperAdminSessionsPage />} />
                          <Route path="time-savings" element={<SuperAdminTimeSavingsPage />} />
                          <Route path="data-retention" element={<SuperAdminDataRetentionPage />} />
                          <Route path="infoboard" element={<SuperAdminInfoboardPage />} />
                          <Route path="shop-stats" element={<SuperAdminShopStatsPage />} />
                          <Route path="system-map" element={<SuperAdminSystemMapPage />} />
                        </Route>
                        {/* Redirect old super-admin URL */}
                        <Route
                          path="/admin/super-dashboard"
                          element={<Navigate to="/admin/super" replace />}
                        />
                        <Route
                          path="/admin/meal-statistics"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin"]}>
                              <Suspense fallback={<div className="flex items-center justify-center h-64">Caricamento statistiche pasti...</div>}>
                                <MealStatisticsDashboard />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        {/* Redirect old Ramadan URL */}
                        <Route
                          path="/admin/ramadan"
                          element={<Navigate to="/amministrazione/ramadan" replace />}
                        />
                        {/* Redirect old CFA URL */}
                        <Route
                          path="/admin/cfa"
                          element={<Navigate to="/admin/super/cfa" replace />}
                        />
                        {/* Redirect old CFA detail URL */}
                        <Route
                          path="/admin/cfa/:cfaId"
                          element={<CfaDetailRedirect />}
                        />
                        {/* Capogruppo — Sidebar layout with nested routes */}
                        <Route
                          path="/capogruppo"
                          element={
                            <ProtectedRoute requiredRoles={["capogruppo", "admin", "super-admin"]}>
                              <CapogruppoLayout />
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<CapogruppoPanoramicaPage />} />
                          <Route path="task" element={<CapogruppoTaskPage />} />
                          <Route path="scabbia" element={<CapogruppoScabbiaPage />} />
                          <Route path="stock" element={<CapogruppoStockPage />} />
                          <Route path="infoboard" element={<CapogruppoInfoboardPage />} />
                          <Route path="todo-operativi" element={<CapogruppoTaskOperativiPage />} />
                        </Route>
                        {/* Redirect old capogruppo URL */}
                        <Route
                          path="/capogruppo/dashboard"
                          element={<Navigate to="/capogruppo" replace />}
                        />
                        {/* Amministrazione — Sidebar layout with nested routes */}
                        <Route
                          path="/amministrazione"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "amministrazione", "shop", "collaboratore_admin"]}>
                              <AmministrazioneLayout />
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<ProtectedRoute requiredModule="admin_overview"><AmministrazioneOverviewPage /></ProtectedRoute>} />
                          <Route path="ra" element={<ProtectedRoute requiredModule="admin_ra"><AmministrazioneRaPage /></ProtectedRoute>} />
                          <Route path="prodotti" element={<ProtectedRoute requiredModule="admin_prodotti"><AmministrazioneProdottiPage /></ProtectedRoute>} />
                          <Route path="vendite" element={<ProtectedRoute requiredModule="admin_vendite"><AmministrazioneVenditePage /></ProtectedRoute>} />
                          <Route path="abbigliamento" element={<ProtectedRoute requiredModule="admin_abbigliamento"><AmministrazioneAbbigliamentoPage /></ProtectedRoute>} />
                          <Route path="transfer" element={<ProtectedRoute requiredModule="admin_transfer"><AmministrazioneTransferPage /></ProtectedRoute>} />

                          <Route path="elenchi" element={<ProtectedRoute requiredModule="admin_elenchi"><AmministrazioneElenchiPage /></ProtectedRoute>} />
                          <Route path="report" element={<ProtectedRoute requiredModule="admin_report"><AmministrazioneReportPage /></ProtectedRoute>} />
                          <Route path="mensa" element={<ProtectedRoute requiredModule="admin_report"><AmministrazioneMensaPage /></ProtectedRoute>} />
                          <Route path="ramadan" element={<ProtectedRoute requiredModule="admin_ramadan"><AmministrazioneRamadanPage /></ProtectedRoute>} />
                          <Route path="anomalie" element={<ProtectedRoute requiredModule="admin_anomalie"><AdminAnomalyPanel /></ProtectedRoute>} />
                          <Route path="todo" element={<ProtectedRoute requiredModule="admin_todo"><AmministrazioneTaskOperativiPage /></ProtectedRoute>} />
                          <Route path="cartellini" element={<ProtectedRoute requiredModule="admin_cartellini"><AmministrazioneTempCardsPage /></ProtectedRoute>} />
                          <Route path="appuntamenti" element={<ProtectedRoute requiredModule="admin_appuntamenti"><AppointmentsManagement /></ProtectedRoute>} />
                          <Route path="appuntamenti/nuovo" element={<ProtectedRoute requiredModule="admin_appuntamenti"><AppointmentCreatePage /></ProtectedRoute>} />
                          <Route path="appuntamenti/import-rl" element={<ProtectedRoute requiredModule="admin_appuntamenti"><AppointmentImportRLPage /></ProtectedRoute>} />
                          <Route path="appuntamenti/import-sem" element={<ProtectedRoute requiredModule="admin_appuntamenti"><AppointmentImportSEMPage /></ProtectedRoute>} />
                          <Route path="appuntamenti/categorie" element={
                            <ProtectedRoute requiredModule="admin_appuntamenti">
                              <ProtectedRoute requiredRoles={["admin", "super-admin"]}>
                                <AppointmentCategoriesAdmin />
                              </ProtectedRoute>
                            </ProtectedRoute>
                          } />
                        </Route>
                        {/* Educatori — Sidebar layout with nested routes */}
                        <Route
                          path="/educatori"
                          element={
                            <ProtectedRoute requiredRoles={["educatore", "admin", "super-admin"]}>
                              <EducatoriLayout />
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<EducatoriOverviewPage />} />
                          <Route path="ra" element={<EducatoriRaPage />} />
                          <Route path="ra/:raId" element={<EducatoriRaDetailPage />} />
                          <Route path="igiene" element={<EducatoriHygienePage />} />
                          <Route path="igiene/:watchlistId" element={<EducatoriWatchlistDetailPage />} />
                        </Route>
                        <Route
                          path="/email-procedures"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "educatore", "capogruppo"]}>
                              <EmailProceduresPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/richiedenti-asilo"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "amministrazione", "capogruppo", "educatore", "assistente_rmna", "assistente"]}>
                              <RichiedentiAsilo />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/test-encryption"
                          element={
                            <ProtectedRoute requiredRoles={["super-admin", "amministrazione"]}>
                              <Suspense fallback={<div>Loading test component...</div>}>
                                <TestEncryption />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/test-crypto"
                          element={
                            <ProtectedRoute requiredRoles={["super-admin", "amministrazione"]}>
                              <Suspense fallback={<div>Loading crypto test component...</div>}>
                                <TestCrypto />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/richiedenti-asilo/:raId"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "amministrazione", "capogruppo", "educatore", "assistente_rmna", "assistente"]}>
                              <RADetailPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/richiedenti-asilo/nuovo"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "amministrazione", "capogruppo", "educatore", "assistente_rmna", "assistente"]}>
                              <RACreatePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/richiedenti-asilo/:raId/edit"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "amministrazione", "capogruppo", "educatore", "assistente_rmna", "assistente"]}>
                              <RAEditPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop"
                          element={
                            <ProtectedRoute>
                              <Shop />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/ra/:raId"
                          element={
                            <ProtectedRoute>
                              <ShopRADetail />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/consegna-prodotti"
                          element={
                            <ProtectedRoute>
                              <ShopConsegnaProdotti />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/biglietti-treno"
                          element={
                            <ProtectedRoute>
                              <ShopBigliettiTreno />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/mobile/biglietti-treno"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "capogruppo", "biglietti_treno"]}>
                              <MobileBigliettiTreno />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/mobile/biglietti-treno-group"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "capogruppo", "biglietti_treno"]}>
                              <MobileBigliettiTrenoGroup />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/lavanderia"
                          element={
                            <ProtectedRoute>
                              <ShopLavanderia />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/gestione-prodotti/nuovo"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin"]}>
                              <GestioneProdottiEdit />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/gestione-prodotti/:id/edit"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin"]}>
                              <GestioneProdottiEdit />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/laundry-management/storico"
                          element={
                            <ProtectedRoute>
                              <LaundryNetHistory />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/laundry-management"
                          element={
                            <ProtectedRoute>
                              <ErrorBoundary fallbackMessage="Si e verificato un errore nella gestione delle reti lavanderia. Ricarica la pagina per continuare.">
                                <LaundryManagementV2 />
                              </ErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/laundry-management-v2"
                          element={
                            <ProtectedRoute>
                              <ErrorBoundary fallbackMessage="Si e verificato un errore nella gestione delle reti lavanderia V2. Ricarica la pagina per continuare.">
                                <LaundryManagement />
                              </ErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/laundry/forgotten-tickets"
                          element={
                            <ProtectedRoute>
                              <LaundryForgottenTickets />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/shop/transfer-verification"
                          element={
                            <ProtectedRoute>
                              <TransferVerificationPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/gestione-restituzioni"
                          element={
                            <ProtectedRoute>
                              <GestioneRestituzioni />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/statistiche"
                          element={
                            <ProtectedRoute>
                              <ShopStatistiche />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/ticket-inventory"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin"]}>
                              <TicketInventoryManagement />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/shop/group-delivery"
                          element={
                            <ProtectedRoute>
                              <ShopGroupDelivery />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/clothing/stock"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "assistente", "educatore", "capogruppo", "assistente_rmna"]}>
                              <ClothingStockPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/clothing/requests"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "assistente", "educatore", "capogruppo", "assistente_rmna"]}>
                              <ClothingRequestsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/clothing/requests/new"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "assistente", "educatore", "capogruppo", "assistente_rmna"]}>
                              <ClothingRequestCreatePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/clothing/arrival/:raProfileId"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "assistente", "educatore", "capogruppo", "assistente_rmna"]}>
                              <ClothingArrivalRegistrationPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/test-circular-deps"
                          element={
                            <Suspense fallback={<div>Loading test...</div>}>
                              <TestCircularDeps />
                            </Suspense>
                          }
                        />
                        {/* Redirect old appointments URLs */}
                        <Route path="/appointments" element={<Navigate to="/amministrazione/appuntamenti" replace />} />
                        <Route path="/appointments/*" element={<Navigate to="/amministrazione/appuntamenti" replace />} />
                        {/* GEP Routes */}
                        <Route
                          path="/gep"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "capogruppo"]}>
                              <GepDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/gep/eligible"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "capogruppo"]}>
                              <GepEligibleList />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/gep/activities"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin"]}>
                              <GepActivitiesConfig />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/gep/activity-types"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin"]}>
                              <GepActivityTypesManagement />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/gep/daily-categories"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin"]}>
                              <GepDailyActivityCategoriesManagement />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/gep/collaborators"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin"]}>
                              <GepCollaboratorsManagement />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/gep/work-days"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "capogruppo"]}>
                              <GepWorkDayRegistration />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/gep/work-days/record"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "capogruppo"]}>
                              <GepWorkDayRegistration />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/gep/activities/record"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "capogruppo", "shop"]}>
                              <GepActivityRecordPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/gep/ra-points"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "capogruppo"]}>
                              <GepRAPointsList />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/gep/config"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin"]}>
                              <GepConfigAdmin />
                            </ProtectedRoute>
                          }
                        />
                        {/* Distribution Routes */}
                        <Route
                          path="/distribuzione"
                          element={
                            <ProtectedRoute requiredRoles={["admin", "super-admin", "amministrazione"]}>
                              <DistributionPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                        <Route path="*" element={<NotFound />} />
                      </Route>
                    </Routes>
                    </Suspense>
                    </LazyOfflineBoundary>
                  </ImpersonationProvider>
                </MutationQueueProvider>

            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </AccessibilityProvider>
    </ThemeProvider>
  </PersistQueryClientProvider>
);

export default App;
